import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { Users, Plus, KeyRound, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["school_admin", "academic", "finance", "teacher", "parent", "student", "driver", "hr"];

const EMPTY = { email: "", password: "", name: "", role: "parent", phone: "" };

export default function UsersPage() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [roleFilter, setRoleFilter] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const [pwOpen, setPwOpen] = useState(null);
  const [pw, setPw] = useState("");

  const load = () => {
    const url = roleFilter === "all" ? "/users" : `/users?role=${roleFilter}`;
    api.get(url).then(r => setItems(r.data || []));
  };
  useEffect(() => { load(); }, [roleFilter]);

  const create = async () => {
    try {
      await api.post("/auth/register", { ...form, school_id: "demo-school-1" });
      toast.success(t("success"));
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (u) => {
    if (!window.confirm(`${t("delete") || "Delete"} ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success(t("success")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const resetPw = async () => {
    if (!pw || pw.length < 6) { toast.error("min 6 chars"); return; }
    try { await api.put(`/users/${pwOpen.id}`, { password: pw }); toast.success(t("success")); setPwOpen(null); setPw(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const filtered = q
    ? items.filter(u => (u.email || "").toLowerCase().includes(q.toLowerCase()) || (u.name || "").toLowerCase().includes(q.toLowerCase()))
    : items;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-3"><Users className="w-7 h-7 text-primary" /> {t("user_accounts")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute top-2.5 start-2.5 text-muted-foreground" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder={t("search") || "Search"} className="ps-9 w-56" data-testid="users-search" />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-44" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all") || "All"}</SelectItem>
              {ROLES.map(r => <SelectItem key={r} value={r}>{t(r) || r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-user-btn"><Plus className="w-4 h-4 me-2" />{t("new_user")}</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("new_user")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("name") || "Name"}</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
                <div><Label>{t("email")}</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
                <div><Label>{t("phone")}</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>{t("role") || "Role"}</Label>
                  <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{t(r) || r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{t("password") || "Password"}</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="user-pw" autoComplete="new-password" /></div>
                <Button onClick={create} className="w-full" data-testid="user-save">{t("save")}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border shadow-none">
        <ul className="divide-y divide-border">
          {filtered.map(u => (
            <li key={u.id} className="p-4 flex items-center gap-3 hover:bg-muted/30" data-testid={`user-${u.id}`}>
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center font-semibold">{(u.email || "?")[0].toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{u.name || u.email}</div>
                <div className="text-xs text-muted-foreground truncate">{u.email} · <span className="font-medium">{t(u.role) || u.role}</span>{u.phone ? ` · ${u.phone}` : ""}{u.seeded ? " · seeded" : ""}</div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!u.active} onCheckedChange={() => toggleActive(u)} data-testid={`user-active-${u.id}`} />
                <Button size="sm" variant="ghost" onClick={() => { setPwOpen(u); setPw(""); }} title={t("reset_password")} data-testid={`user-reset-${u.id}`}><KeyRound className="w-4 h-4" /></Button>
                {!u.seeded && (
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(u)} data-testid={`user-del-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
                )}
              </div>
            </li>
          ))}
          {filtered.length === 0 && <li className="p-12 text-center text-muted-foreground">{t("no_data")}</li>}
        </ul>
      </Card>

      <Dialog open={!!pwOpen} onOpenChange={(o) => !o && setPwOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("reset_password")} — {pwOpen?.email}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="••••••" autoComplete="new-password" data-testid="reset-pw-input" />
            <Button onClick={resetPw} className="w-full" data-testid="reset-pw-submit">{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
